/**
 * The program control in the AppBar.
 *
 * Deliberately the only way to change program. Everything below the bar is
 * about one program, and a stray click that silently swapped it would be
 * indistinguishable from the model having changed under you.
 *
 * The branch sits beside it as a label rather than a menu: until BEST reads
 * and writes GitLab, offering a branch to switch to would offer a database
 * that has never been imported.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CheckIcon from '@mui/icons-material/Check'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import Divider from '@mui/material/Divider'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ConfirmDialog from './ConfirmDialog'
import CreateProjectDialog from './CreateProjectDialog'
import ImportDialog from './ImportDialog'
import { ApiError, api } from '../../api'
import { installRole, usePrincipal } from '../../state/AppData'
import { viewedBaseline } from '../../state/baselineRef'
import { useProject } from '../../state/Project'
import { useToasts } from '../../state/Toasts'

export default function ProjectPicker() {
  const navigate = useNavigate()
  const principal = usePrincipal()
  const { projects, current, branch, issue, singleDatabase, unsaved, choose } = useProject()
  const { succeeded, failed } = useToasts()

  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [pending, setPending] = useState<string>('')
  const [busy, setBusy] = useState(false)
  // Viewing a baseline (issue #97): a fixed, read-only copy of a tag. Neither of the
  // two writing items below means anything there, so both say why they are off.
  const baseline = viewedBaseline(principal.baseline, branch)

  // Nothing to pick between, and nothing the registry knows about: showing an
  // empty menu would suggest the feature is broken rather than unused.
  if (singleDatabase || !current) return null

  const swap = async (slug: string) => {
    setBusy(true)
    try {
      await choose(slug)
      // A hard context switch: the current URL may name something that does
      // not exist in the program being switched to.
      navigate('/')
    } finally {
      setBusy(false)
      setPending('')
      setAnchor(null)
    }
  }

  const normalize = async () => {
    if (!current) return
    setBusy(true)
    try {
      const result = await api.normalize(current.slug)
      succeeded(
        result.already_normalized
          ? 'Already normalized — nothing to change.'
          : `Normalized ${result.files.length} file(s) on ${result.ref}. ` +
            'Saves from here on are ordinary line diffs.',
      )
    } catch (error) {
      failed(error instanceof ApiError ? error.message : 'Could not normalize.')
    } finally {
      setBusy(false)
    }
  }

  const select = (slug: string) => {
    if (slug === current.slug) {
      setAnchor(null)
      return
    }
    if (unsaved) {
      setPending(slug)
      return
    }
    void swap(slug)
  }

  return (
    <>
      <Button
        color="inherit"
        onClick={(event) => setAnchor(event.currentTarget)}
        startIcon={<AccountTreeIcon />}
        endIcon={<ExpandMoreIcon />}
        aria-label={`Program: ${current.name}. Change program`}
        sx={{ textTransform: 'none', maxWidth: 320 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {current.name}
          </Typography>
          {/* The branch belongs to WorkPicker, next to the issue it is for. */}
          {unsaved ? (
            <Tooltip title="Unsaved changes" describeChild>
              <Box
                component="span"
                aria-label="Unsaved changes"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'warning.main',
                  flexShrink: 0,
                }}
              />
            </Tooltip>
          ) : null}
        </Box>
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {projects.map((project) => (
          <MenuItem
            key={project.slug}
            selected={project.slug === current.slug}
            disabled={busy}
            onClick={() => select(project.slug)}
          >
            <ListItemIcon>
              {project.slug === current.slug ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText
              primary={project.name}
              secondary={project.last_branch || project.default_branch}
            />
            {project.file_count === 0 ? (
              <Chip label="empty" size="small" sx={{ ml: 2 }} />
            ) : null}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          disabled={!principal.can_write || Boolean(baseline)}
          onClick={() => {
            setAnchor(null)
            setImporting(true)
          }}
        >
          <ListItemIcon>
            <CloudDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Import from GitLab…"
            secondary={
              baseline ? 'Read-only baseline' : principal.can_write ? undefined : 'Read-only access'
            }
          />
        </MenuItem>
        <MenuItem
          disabled={!principal.can_write || !issue || Boolean(baseline)}
          onClick={() => {
            setAnchor(null)
            void normalize()
          }}
        >
          <ListItemIcon>
            <FormatAlignLeftIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Normalize formatting…"
            secondary={
              baseline
                ? 'Read-only baseline'
                : !principal.can_write
                ? 'Read-only access'
                : !issue
                  ? 'Start work first — BEST will not write to the default branch'
                  : 'One reviewable commit; saves are line diffs afterwards'
            }
          />
        </MenuItem>
        <MenuItem
          disabled={installRole(principal) !== 'admin'}
          onClick={() => {
            setAnchor(null)
            setCreating(true)
          }}
        >
          <ListItemText
            primary="New program…"
            secondary={installRole(principal) !== 'admin' ? 'Needs admin' : undefined}
          />
        </MenuItem>
      </Menu>

      <ImportDialog open={importing} onClose={() => setImporting(false)} />

      <CreateProjectDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => navigate('/')}
      />

      <ConfirmDialog
        open={Boolean(pending)}
        title="Switch program?"
        message={
          'You have changes that have not been saved.\n\n' +
          'BEST does not keep unsaved work for a program it is not showing, ' +
          'so switching will discard them.'
        }
        confirmLabel="Discard and switch"
        severity="warning"
        busy={busy}
        onConfirm={() => void swap(pending)}
        onCancel={() => setPending('')}
      />
    </>
  )
}
